import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import Layout from './components/Layout';
import ResponsiveShadow from './components/ResponsiveShadow';
import PageHeader from './components/PageHeader';
import PageContent from './components/PageContent';
import PaginationBar from './components/PaginationBar';
import useDefaultModal from './components/hooks/useDefaultModal';
import usePagination10 from './components/hooks/usePagination10';
import DefaultModal from './components/modals/DefaultModal';
import LoadingModal from './components/modals/LoadingModal';
import Toolbar from './components/Toolbar';
import { API_BASE_URL } from '../config/ApiConfig';
import { fndeService } from './api/fndeService';
import useReferenceData from './components/hooks/useReferenceData';

function parseMoneyBR(value) {
  if (value == null || value === '' || value === '-') return 0;
  if (typeof value === 'number') return value;
  const normalized = String(value).replace(/\s/g, '').replace(/[R$]/g, '').replace(/\./g, '').replace(',', '.');
  const n = Number(normalized);
  return Number.isNaN(n) ? 0 : n;
}

function formatCurrency(value) {
  return Number(value || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
}

function normalizeContaMatch(value) {
  const normalized = String(value || '').trim().toUpperCase().replace(/[^0-9A-Z]/g, '').replace(/^0+(?=[0-9A-Z])/g, '');
  return normalized || '0';
}

function getContaCadastroValue(conta) {
  return conta?.numConta || conta?.idConta || conta?.conta || '';
}

function keyOf(item) {
  return `${item.ano}_${item.mes}_${item.idPrograma}_${item.idEscola}_${item.idConta}`;
}

export default function SaldoFndeMensal() {
  const token = localStorage.getItem('token');
  const cnpj = localStorage.getItem('cnpj');
  const ano = localStorage.getItem('ano');
  const { programas, contas } = useReferenceData(token, cnpj);

  const [items, setItems] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isImporting, setIsImporting] = useState(false);
  const anoAtual = new Date().getFullYear();
  const mesAtual = new Date().getMonth() + 1;
  const [mesFiltro, setMesFiltro] = useState(Number(ano) === anoAtual ? mesAtual : 12);
  const { setDefaultModal, showDefaultModal } = useDefaultModal();

  const contasNormalizadas = React.useMemo(() => {
    return (contas || []).map(conta => ({
      original: conta,
      matchKey: normalizeContaMatch(getContaCadastroValue(conta)),
    }));
  }, [contas]);

  const sortedItems = React.useMemo(() => {
    const copy = Array.isArray(items) ? [...items] : [];
    copy.sort((a, b) => {
      if (a.mes !== b.mes) return a.mes - b.mes;
      return String(a.idConta || '').localeCompare(String(b.idConta || ''), 'pt-BR', { sensitivity: 'base' });
    });
    return copy;
  }, [items]);

  const filteredItems = sortedItems.filter(i => i.mes === mesFiltro);
  const { setPagination } = usePagination10(filteredItems.length);
  const displayed = filteredItems.slice(setPagination.startIndex, setPagination.startIndex + setPagination.rowsPerPage);

  const resumoPorPrograma = React.useMemo(() => {
    const map = {};
    (Array.isArray(filteredItems) ? filteredItems : []).forEach(it => {
      const chave = String(it.idPrograma);
      if (!map[chave]) map[chave] = { idPrograma: it.idPrograma, cdb: 0, poupanca: 0, fundos: 0, conta: 0, qtd: 0 };
      map[chave].cdb += Number(it.cdb || 0);
      map[chave].poupanca += Number(it.poupanca || 0);
      map[chave].fundos += Number(it.fundos || 0);
      map[chave].conta += Number(it.conta || 0);
      map[chave].qtd += 1;
    });
    return Object.values(map);
  }, [filteredItems]);

  useEffect(() => {
    if (!token || !cnpj || !ano) { setIsLoading(false); return; }
    setIsLoading(true);
    axios.get(`${API_BASE_URL.SALDO_FNDE_MENSAL}`, {
      params: { idEscola: cnpj, ano },
      headers: { Authorization: token ? `Bearer ${token}` : undefined },
    })
      .then(r => setItems(Array.isArray(r.data) ? r.data : []))
      .catch(() => { setItems([]); showDefaultModal('error', 'Erro ao carregar saldos FNDE mensais.'); })
      .finally(() => setIsLoading(false));
  }, [token, cnpj, ano]);

  // No ano atual, seleciona o último mês que possui dados
  useEffect(() => {
    if (Number(ano) !== anoAtual) return;
    const mesesComDados = [...new Set((items || []).map(i => i.mes))].filter(Boolean);
    if (mesesComDados.length) {
      setMesFiltro(Math.max(...mesesComDados));
    }
  }, [items, ano]);

  async function handleImportarFnde() {
    if (!cnpj || !ano) return;
    setIsImporting(true);
    try {
      const anoBase = Number(ano);
      const allPayload = [];
      const mesesImportados = [];
      const naoMapeadas = [];

      for (let m = 1; m <= 12; m++) {
        const mesStr = String(m).padStart(2, '0');
        const alvo = `${mesStr}-${ano}`;
        let resp;
        try {
          resp = await fndeService.buscarSaldoEntidade(alvo, cnpj);
        } catch (e) {
          continue;
        }
        if (!resp?.data?.sucesso) continue;
        const dadosAlvo = Array.isArray(resp.data?.dados) ? resp.data.dados : [];
        if (!dadosAlvo.length) continue;

        mesesImportados.push(mesStr);

        for (const row of dadosAlvo) {
          const contaLabel = String(row['Conta'] || row['Número Conta'] || row['Num Conta'] || '').trim();
          if (!contaLabel) continue;
          const contaMatchKey = normalizeContaMatch(contaLabel);
          const contaObj = contasNormalizadas.find(c => c.matchKey === contaMatchKey)?.original;
          if (!contaObj || !contaObj.idPrograma) {
            naoMapeadas.push(contaLabel);
            continue;
          }
          allPayload.push({
            ano: anoBase,
            mes: m,
            idPrograma: contaObj.idPrograma,
            idEscola: cnpj,
            idConta: getContaCadastroValue(contaObj),
            cdb: parseMoneyBR(row['Saldo RDB/CDB']),
            poupanca: parseMoneyBR(row['Saldo Poupança'] || row['Saldo Poupana']),
            fundos: parseMoneyBR(row['Saldo Fundos']),
            conta: parseMoneyBR(row['Saldo Conta']),
          });
        }
      }

      if (!allPayload.length) {
        showDefaultModal('warning', 'Nenhum dado retornado pelo FNDE para importação.');
        return;
      }

      await axios.post(`${API_BASE_URL.SALDO_FNDE_MENSAL}/lote`, allPayload, {
        headers: { Authorization: token ? `Bearer ${token}` : undefined },
      });

      let mensagem = `${allPayload.length} registro(s) importado(s) dos meses ${mesesImportados.join(', ')}/${ano} e salvo(s) com sucesso.`;
      if (naoMapeadas.length) {
        mensagem += `\n${naoMapeadas.length} conta(s) não mapeada(s): ${naoMapeadas.slice(0, 10).join(', ')}`;
      }
      showDefaultModal('success', mensagem);
      // refresh
      const r = await axios.get(`${API_BASE_URL.SALDO_FNDE_MENSAL}`, {
        params: { idEscola: cnpj, ano },
        headers: { Authorization: token ? `Bearer ${token}` : undefined },
      });
      setItems(Array.isArray(r.data) ? r.data : []);
    } catch (error) {
      showDefaultModal('error', `Erro ao importar saldos FNDE mensais: ${error?.message || error}`);
    } finally {
      setIsImporting(false);
    }
  }

  function programaLabel(idPrograma) {
    const p = (programas || []).find(x => String(x.id) === String(idPrograma));
    return p ? p.descricao : idPrograma;
  }

  function contaLabel(idConta) {
    const c = (contas || []).find(x => String(x.numConta) === String(idConta) || String(x.idConta) === String(idConta));
    return c ? (c.nomeConta || c.numConta || c.idConta) : idConta;
  }

  function totalLinha(item) {
    return Number(item.cdb || 0) + Number(item.poupanca || 0) + Number(item.fundos || 0) + Number(item.conta || 0);
  }

  const meses = ['Jan','Fev','Mar','Abr','Mai','Jun','Jul','Ago','Set','Out','Nov','Dez'];
  const mesesComDados = [...new Set((items || []).map(i => i.mes))].filter(Boolean).sort((a, b) => a - b);



  return (
    <Layout>
      <LoadingModal show={isImporting} />
      <ResponsiveShadow>
        <PageHeader title="SALDO FNDE MENSAL" icon="bi-bank" />
        <PageContent>
          <Toolbar>
            <button className="btn btn-primary btn-sm botao-igual" onClick={handleImportarFnde} disabled={isImporting}>
              <i className="bi bi-cloud-download me-1"></i>Importar FNDE
            </button>
            <div className="btn-group btn-group-sm ms-2" role="group">
              {(mesesComDados.length ? mesesComDados : meses.map((_, i) => i + 1)).map(m => (
                <button key={m} type="button" className={`btn btn-sm ${mesFiltro === m ? 'btn-success' : 'btn-outline-success'}`} onClick={() => setMesFiltro(m)}>{meses[m - 1]}</button>
              ))}
            </div>
          </Toolbar>

          {resumoPorPrograma.length > 0 && (
            <div className="mb-2 d-flex flex-wrap gap-2" style={{ maxHeight: '30vh', overflowY: 'auto', overflowX: 'hidden', flexShrink: 0 }}>
              {resumoPorPrograma.map(prog => (
                <div className="card border-0 shadow-sm" key={String(prog.idPrograma)} style={{ borderRadius: 10, flex: '1 1 0', minWidth: 200, maxWidth: '25%' }}>
                  <div className="card-body pt-2 pb-1 px-3">
                    <div className="d-flex justify-content-between align-items-center gap-2" style={{ backgroundColor: '#198754', color: '#fff', margin: '-0.5rem -1rem 0.5rem', padding: '0.15rem 1rem', borderRadius: '10px 10px 0 0', minHeight: '22px' }}>
                      <span className="fw-bold text-truncate" style={{ fontSize: '.62rem' }} title={prog.idPrograma}>{programaLabel(prog.idPrograma)}</span>
                      <span className="badge text-bg-light text-success flex-shrink-0" style={{ fontSize: '.62rem' }}>{prog.qtd}</span>
                    </div>
                    <div className="d-flex justify-content-between" style={{ fontSize: '.72rem', whiteSpace: 'nowrap' }}>
                      <span className="text-muted">RDB/CDB</span><span>{formatCurrency(prog.cdb)}</span>
                    </div>
                    <div className="d-flex justify-content-between" style={{ fontSize: '.72rem', whiteSpace: 'nowrap' }}>
                      <span className="text-muted">Poupança</span><span>{formatCurrency(prog.poupanca)}</span>
                    </div>
                    <div className="d-flex justify-content-between" style={{ fontSize: '.72rem', whiteSpace: 'nowrap' }}>
                      <span className="text-muted">Fundos</span><span>{formatCurrency(prog.fundos)}</span>
                    </div>
                    <div className="d-flex justify-content-between" style={{ fontSize: '.72rem', whiteSpace: 'nowrap' }}>
                      <span className="text-muted">Conta</span><span>{formatCurrency(prog.conta)}</span>
                    </div>
                    <div className="d-flex justify-content-between pt-1 mt-1 border-top fw-bold" style={{ fontSize: '.78rem', whiteSpace: 'nowrap' }}>
                      <span>Total</span><span className="text-success">{formatCurrency(prog.cdb + prog.poupanca + prog.fundos + prog.conta)}</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {isLoading ? (
            <div className="text-center py-3"><span className="spinner-border spinner-border-sm"></span></div>
          ) : (
            <div className="table-responsive">
              <table className="table table-hover table-sm" style={{ fontSize: '0.75rem' }}>
                <thead className="table-dark" style={{ position: 'sticky', top: 0, zIndex: 2 }}>
                  <tr>
                    <th>#</th>
                    <th>Programa</th>
                    <th>Conta</th>
                    <th className="text-end">RDB/CDB</th>
                    <th className="text-end">Poupança</th>
                    <th className="text-end">Fundos</th>
                    <th className="text-end">Conta</th>
                    <th className="text-end">Total</th>
                  </tr>
                </thead>
                <tbody>
                  {displayed.map((item, idx) => (
                    <tr key={keyOf(item)}>
                      <td>{setPagination.startIndex + idx + 1}</td>
                      <td>{programaLabel(item.idPrograma)}</td>
                      <td>{contaLabel(item.idConta)}</td>
                      <td className="text-end">{formatCurrency(item.cdb)}</td>
                      <td className="text-end">{formatCurrency(item.poupanca)}</td>
                      <td className="text-end">{formatCurrency(item.fundos)}</td>
                      <td className="text-end">{formatCurrency(item.conta)}</td>
                      <td className="text-end fw-bold text-success">{formatCurrency(totalLinha(item))}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </PageContent>
        <PaginationBar setPagination={setPagination} />
      </ResponsiveShadow>
      <DefaultModal {...setDefaultModal} />
    </Layout>
  );
}